import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Building2, Trash2, MapPin } from 'lucide-react';
import { usePermissions } from './usePermissions';
import ListPageShell from './list/ListPageShell';
import SortableTh from './list/SortableTh';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const OrganizationsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const { canCreate, canDelete } = usePermissions('organizations');

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'org_id', direction: 'asc' });

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/organizations');
      setOrgs(res.data || []);
    } catch (err) {
      console.error('Fetch Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filteredOrgs = orgs.filter(org =>
    (org.org_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (org.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedOrgs = [...filteredOrgs].sort((a, b) => {
    const valA = (a[sortConfig.key] || '').toString().toLowerCase();
    const valB = (b[sortConfig.key] || '').toString().toLowerCase();
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleDeleteClick = (e, org) => {
    e.stopPropagation();
    Swal.fire({
      ...swalTheme(),
      title: t('common.areYouSure', 'Are you sure?'),
      text: t('organizations.deleteConfirmText', `You are about to permanently delete organization ${org.org_id}. This action cannot be undone!`),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yesDelete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/organizations/${org.org_id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        Swal.fire({
          ...swalTheme(),
          icon: 'success',
          title: t('common.deleted', 'Deleted!'),
          text: t('organizations.deleteSuccess', 'Organization has been deleted successfully.'),
          confirmButtonText: t('common.ok', 'OK'),
        });
        fetchOrgs();
      } catch (err) {
        Swal.fire({
          ...swalTheme(),
          icon: 'error',
          title: t('common.error', 'Error!'),
          text: err.response?.data?.detail || t('organizations.deleteFail', 'Failed to delete organization.'),
          confirmButtonText: t('common.ok', 'OK'),
        });
      }
    });
  };

  return (
    <ListPageShell
      icon={<Building2 size={20} />}
      title={t('organizations.title')}
      loading={loading}
      onRefresh={fetchOrgs}
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={t('common.search')}
      newButtonLabel={t('organizations.new', 'NEW ORGANIZATION')}
      onNew={() => navigate('/organizations/new')}
      canCreate={canCreate}
      isRTL={isRTL}
    >
      <table className="w-full text-left border-collapse">
        <thead className="bg-primary-dark text-white font-black uppercase sticky top-0 z-30 shadow-md">
          <tr className={isRTL ? 'flex-row-reverse' : ''}>
            <SortableTh label={t('organizations.org_id')} sortKey="org_id" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} />
            <SortableTh label={t('organizations.description')} sortKey="description" sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} />
            <th className="p-3 border-r border-white/10 text-center w-28">{t('menu.sites', 'Sites')}</th>
            <th className="p-3 border-r border-white/10 text-center w-28">{t('organizations.active')}</th>
            <th className="p-3 text-center w-16"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-subtle">
          {loading ? (
            <tr><td colSpan="5" className="p-20 text-center italic text-primary font-black animate-pulse text-sm uppercase tracking-widest bg-surface-panel">{t('workflow.loading')}</td></tr>
          ) : sortedOrgs.length === 0 ? (
            <tr><td colSpan="5" className="p-20 text-center italic text-ink-secondary text-xs font-black uppercase bg-surface-panel border-dashed border-2 m-4">{t('dashboard.no_data')}</td></tr>
          ) : sortedOrgs.map((org) => (
            <tr
              key={org.org_id}
              onClick={() => navigate(`/organizations/${org.org_id}`)}
              className={`hover:bg-primary-soft cursor-pointer group transition-all ${isRTL ? 'text-right' : ''}`}
            >
              <td className="p-3.5 border-r border-line-subtle font-black text-primary uppercase group-hover:text-primary-hover transition-colors">{org.org_id}</td>
              <td className="p-3.5 border-r border-line-subtle text-ink font-bold">{org.description}</td>
              <td className="p-3.5 border-r border-line-subtle text-center">
                <span className="inline-flex items-center gap-1 text-ink-secondary font-black">
                  <MapPin size={12} /> {org.sites?.length || 0}
                </span>
              </td>
              <td className="p-3.5 border-r border-line-subtle text-center">
                <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${org.active ? 'bg-status-success-bg text-status-success' : 'bg-status-danger-bg text-status-danger'}`}>
                  {org.active ? t('common.yes') : t('common.no')}
                </span>
              </td>
              <td className="p-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                {canDelete && (
                  <button onClick={(e) => handleDeleteClick(e, org)} title={t('sidebar.delete')} className="text-status-danger/70 hover:text-status-danger transition-colors">
                    <Trash2 size={14} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListPageShell>
  );
};

export default OrganizationsList;
